import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from '../utils/toast';
import { Save, TestTube, Eye, EyeOff, CheckCircle, AlertCircle, Loader2, MessageCircle, Sparkles, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_OPENAI_MODELS = [
  'gpt-5',
  'gpt-5-mini',
  'gpt-5-nano',
  'gpt-4.1',
  'gpt-4.1-mini',
  'gpt-4.1-nano',
  'gpt-4o',
  'gpt-4o-mini'
];

const DEFAULT_CLAUDE_MODELS = [
  'claude-opus-5',
  'claude-sonnet-5',
  'claude-opus-4-1-20250805',
  'claude-opus-4-20250514',
  'claude-sonnet-4-20250514',
  'claude-3-7-sonnet-20250219',
  'claude-3-5-haiku-20241022'
];

const DEFAULT_AI_TRAINER_PROVIDERS = [
  {
    provider: 'openai',
    provider_name: 'OpenAI (ChatGPT)',
    api_key: '',
    base_url: 'https://api.openai.com/v1',
    organization_id: '',
    project_id: '',
    status: 'Inactive',
    model: 'gpt-4o-mini',
    supported_models: DEFAULT_OPENAI_MODELS,
    behavior_config: ''
  },
  {
    provider: 'anthropic',
    provider_name: 'Anthropic (Claude)',
    api_key: '',
    base_url: 'https://api.anthropic.com',
    organization_id: '',
    project_id: '',
    status: 'Inactive',
    model: 'claude-sonnet-5',
    supported_models: DEFAULT_CLAUDE_MODELS,
    behavior_config: ''
  }
];

const DEFAULT_AI_TRAINER_PROMPT = 'You are an AI Trainer helping hospital staff learn NABH standards.';

const IntegrationSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [s3StatusMessage, setS3StatusMessage] = useState('');
  
  // S3 Settings
  const [formData, setFormData] = useState({
    aws_access_key: '',
    aws_secret_key: '',
    aws_bucket_name: 'exlva-training-content',
    aws_region: 'ap-south-1',
    aws_base_url: ''
  });
  
  const [originalSecretKey, setOriginalSecretKey] = useState('');
  
  // einGenius Settings
  const [einGeniusData, setEinGeniusData] = useState({
    chat_url: 'https://eingenius.ai/',
    api_endpoint: 'https://eingenius.ai/projects/documents/chat_assistant/ms_addin_authentication/',
    project_key: '6fc9c1ab08e55d17505e732d5b832ce5',
    is_enabled: true
  });
  const [einGeniusConfigured, setEinGeniusConfigured] = useState(false);
  const [einGeniusStatusMessage, setEinGeniusStatusMessage] = useState('');
  const [savingEinGenius, setSavingEinGenius] = useState(false);

  // AI Trainer Settings
  const [aiTrainerData, setAiTrainerData] = useState({
    active_provider: 'openai',
    system_prompt: DEFAULT_AI_TRAINER_PROMPT,
    is_enabled: false,
    providers: DEFAULT_AI_TRAINER_PROVIDERS
  });
  const [aiTrainerConfigured, setAiTrainerConfigured] = useState(false);
  const [savingAiTrainer, setSavingAiTrainer] = useState(false);
  const [testingAiTrainer, setTestingAiTrainer] = useState(false);
  const [showAiProviderKey, setShowAiProviderKey] = useState(false);
  const [uploadingAiTrainerIcon, setUploadingAiTrainerIcon] = useState(false);
  
  // SendGrid Settings
  const [sendgridData, setSendgridData] = useState({
    api_key: '',
    sender_email: '',
    sender_name: 'Eptral.Hospital Support',
    reply_to_email: '',
    is_enabled: false
  });
  const [sendgridConfigured, setSendgridConfigured] = useState(false);
  const [sendgridStatusMessage, setSendgridStatusMessage] = useState('');
  const [savingSendgrid, setSavingSendgrid] = useState(false);
  const [testingSendgrid, setTestingSendgrid] = useState(false);
  const [showSendgridKey, setShowSendgridKey] = useState(false);
  const [originalSendgridKey, setOriginalSendgridKey] = useState('');
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      toast.error('Please sign in to access this page');
      navigate('/signin');
      return;
    }
    
    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.role !== 'Super Admin') {
        toast.error('Access denied. Only Super Admin can access Integration Settings.');
        navigate('/dashboard');
        return;
      }
      
      fetchSettings();
      fetchEinGeniusSettings();
      fetchAiTrainerSettings();
      fetchSendgridSettings();
    } catch (error) {
      navigate('/signin');
    }
  }, [navigate]);
  
  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/integration-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormData({
        aws_access_key: response.data.aws_access_key || '',
        aws_secret_key: response.data.aws_secret_key || '',
        aws_bucket_name: response.data.aws_bucket_name || 'exlva-training-content',
        aws_region: response.data.aws_region || 'ap-south-1',
        aws_base_url: response.data.aws_base_url || ''
      });
      
      setOriginalSecretKey(response.data.aws_secret_key || '');
      setIsConfigured(response.data.is_configured || false);
      setS3StatusMessage(response.data.status_message || '');
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      if (!error.response) {
        toast.error('Unable to connect to the server. Please check if the backend service is running.');
      } else if (error.response.status === 403) {
        toast.error('Access denied. Only Super Admin can access Integration Settings.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to load S3 settings. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchEinGeniusSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/integration-settings/eingenius`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEinGeniusData({
        chat_url: response.data.chat_url || 'https://eingenius.ai/',
        api_endpoint: response.data.api_endpoint || 'https://eingenius.ai/projects/documents/chat_assistant/ms_addin_authentication/',
        project_key: response.data.project_key || '6fc9c1ab08e55d17505e732d5b832ce5',
        is_enabled: response.data.is_enabled !== false
      });
      setEinGeniusConfigured(response.data.is_configured || false);
      setEinGeniusStatusMessage(response.data.status_message || '');
    } catch (error) {
      console.error('Failed to fetch einGenius settings:', error);
      setEinGeniusConfigured(false);
      setEinGeniusStatusMessage(error.response?.data?.detail || 'AI configuration missing');
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleTestConnection = async () => {
    if (!formData.aws_access_key || !formData.aws_secret_key) {
      toast.error('Please enter AWS Access Key and Secret Key');
      return;
    }
    
    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/integration-settings/s3/test`,
        {
          aws_access_key: formData.aws_access_key,
          aws_secret_key: formData.aws_secret_key.startsWith('*') ? originalSecretKey : formData.aws_secret_key,
          aws_bucket_name: formData.aws_bucket_name,
          aws_region: formData.aws_region,
          aws_base_url: formData.aws_base_url
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      if (!error.response) {
        toast.error('Unable to connect to the server. Please check if the backend service is running.');
      } else {
        const errorMsg = error.response?.data?.detail || 'S3 configuration could not be validated. Please check bucket name, region, access key, secret key, and permissions.';
        toast.error(errorMsg);
      }
    } finally {
      setTesting(false);
    }
  };
  
  const handleSaveSettings = async () => {
    if (!formData.aws_access_key || !formData.aws_secret_key) {
      toast.error('Please enter AWS Access Key and Secret Key');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // If secret key is masked, use the original
      const secretKey = formData.aws_secret_key.startsWith('*') 
        ? originalSecretKey 
        : formData.aws_secret_key;
      
      // Validate connection before saving
      try {
        const testResponse = await axios.post(
          `${API}/admin/integration-settings/s3/test`,
          {
            aws_access_key: formData.aws_access_key,
            aws_secret_key: secretKey,
            aws_bucket_name: formData.aws_bucket_name,
            aws_region: formData.aws_region,
            aws_base_url: formData.aws_base_url
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!testResponse.data.success) {
          toast.error('S3 configuration could not be validated. Please check bucket name, region, access key, secret key, and permissions.');
          setSaving(false);
          return;
        }
      } catch (testError) {
        const testMsg = testError.response?.data?.detail || 'S3 configuration could not be validated. Please check bucket name, region, access key, secret key, and permissions.';
        toast.error(testMsg);
        setSaving(false);
        return;
      }
      
      await axios.put(
        `${API}/admin/integration-settings/s3`,
        {
          aws_access_key: formData.aws_access_key,
          aws_secret_key: secretKey,
          aws_bucket_name: formData.aws_bucket_name,
          aws_region: formData.aws_region,
          aws_base_url: formData.aws_base_url
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('S3 settings validated and saved successfully');
      setIsConfigured(true);
      setS3StatusMessage('');
      fetchSettings(); // Reload to get masked secret key
    } catch (error) {
      if (!error.response) {
        toast.error('Unable to connect to the server. Please check if the backend service is running.');
      } else {
        const errorMsg = error.response?.data?.detail || 'Failed to save settings';
        toast.error(errorMsg);
      }
    } finally {
      setSaving(false);
    }
  };
  
  const handleEinGeniusChange = (field, value) => {
    setEinGeniusData(prev => ({ ...prev, [field]: value }));
  };

  const mergeAiTrainerSettings = (settings) => {
    const incomingProviders = settings.providers || [];
    const mergedProviders = DEFAULT_AI_TRAINER_PROVIDERS.map(defaultProvider => ({
      ...defaultProvider,
      ...(incomingProviders.find(provider => provider.provider === defaultProvider.provider) || {})
    }));
    incomingProviders.forEach(provider => {
      if (!mergedProviders.some(item => item.provider === provider.provider)) {
        mergedProviders.push(provider);
      }
    });
    return {
      active_provider: settings.active_provider || 'openai',
      system_prompt: settings.system_prompt || DEFAULT_AI_TRAINER_PROMPT,
      is_enabled: settings.is_enabled === true,
      providers: mergedProviders,
      ai_trainer_icon_s3_key: settings.ai_trainer_icon_s3_key || '',
      ai_trainer_icon_url: settings.ai_trainer_icon_url || ''
    };
  };

  const fetchAiTrainerSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/integration-settings/ai-trainer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiTrainerData(mergeAiTrainerSettings(response.data || {}));
      setAiTrainerConfigured(response.data?.is_configured || false);
    } catch (error) {
      console.error('Failed to fetch AI Trainer settings:', error);
      setAiTrainerConfigured(false);
    }
  };

  const activeAiProvider = aiTrainerData.providers.find(provider => provider.provider === aiTrainerData.active_provider) || aiTrainerData.providers[0];

  const updateAiTrainerProvider = (providerKey, field, value) => {
    setAiTrainerData(prev => ({
      ...prev,
      providers: prev.providers.map(provider => (
        provider.provider === providerKey ? { ...provider, [field]: value } : provider
      ))
    }));
  };

  const handleAiTrainerProviderChange = (providerKey) => {
    setAiTrainerData(prev => ({
      ...prev,
      active_provider: providerKey,
      providers: prev.providers.map(provider => ({
        ...provider,
        status: provider.provider === providerKey ? 'Active' : 'Inactive'
      }))
    }));
  };

  const handleAiModelListChange = (providerKey, value) => {
    const models = value
      .split('\n')
      .map(model => model.trim())
      .filter(Boolean);
    updateAiTrainerProvider(providerKey, 'supported_models', models);
    const provider = aiTrainerData.providers.find(item => item.provider === providerKey);
    if (provider && !models.includes(provider.model) && models[0]) {
      updateAiTrainerProvider(providerKey, 'model', models[0]);
    }
  };
  
  // SendGrid functions
  const fetchSendgridSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/integration-settings/sendgrid`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSendgridData({
        api_key: response.data.api_key || '',
        sender_email: response.data.sender_email || '',
        sender_name: response.data.sender_name || 'Eptral.Hospital Support',
        reply_to_email: response.data.reply_to_email || '',
        is_enabled: response.data.is_enabled || false
      });
      setOriginalSendgridKey(response.data.api_key || '');
      setSendgridConfigured(response.data.is_configured || false);
      setSendgridStatusMessage(response.data.status_message || '');
    } catch (error) {
      console.error('Failed to fetch SendGrid settings:', error);
      setSendgridConfigured(false);
      setSendgridStatusMessage(error.response?.data?.detail || 'SendGrid configuration missing');
    }
  };
  
  const handleSendgridChange = (field, value) => {
    setSendgridData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleTestSendgrid = async () => {
    if (!sendgridData.api_key || !sendgridData.sender_email) {
      toast.error('Please enter SendGrid API Key and Sender Email');
      return;
    }
    
    setTestingSendgrid(true);
    try {
      const token = localStorage.getItem('token');
      const apiKey = sendgridData.api_key.includes('*') ? originalSendgridKey : sendgridData.api_key;
      const response = await axios.post(
        `${API}/admin/integration-settings/sendgrid/test`,
        {
          api_key: apiKey,
          sender_email: sendgridData.sender_email,
          sender_name: sendgridData.sender_name,
          reply_to_email: sendgridData.reply_to_email
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'SendGrid connection test failed';
      toast.error(errorMsg);
    } finally {
      setTestingSendgrid(false);
    }
  };
  
  const handleSaveSendgrid = async () => {
    if (!sendgridData.api_key || !sendgridData.sender_email) {
      toast.error('Please enter SendGrid API Key and Sender Email');
      return;
    }
    
    setSavingSendgrid(true);
    try {
      const token = localStorage.getItem('token');
      const apiKey = sendgridData.api_key.includes('*') ? originalSendgridKey : sendgridData.api_key;
      
      await axios.put(
        `${API}/admin/integration-settings/sendgrid`,
        {
          api_key: apiKey,
          sender_email: sendgridData.sender_email,
          sender_name: sendgridData.sender_name,
          reply_to_email: sendgridData.reply_to_email,
          is_enabled: sendgridData.is_enabled
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('SendGrid settings saved successfully');
      setSendgridConfigured(true);
      setSendgridStatusMessage('');
      fetchSendgridSettings();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save SendGrid settings';
      toast.error(errorMsg);
    } finally {
      setSavingSendgrid(false);
    }
  };
  
  const handleSaveEinGeniusSettings = async () => {
    setSavingEinGenius(true);
    try {
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API}/admin/integration-settings/eingenius`,
        {
          chat_url: einGeniusData.chat_url,
          api_endpoint: einGeniusData.api_endpoint,
          project_key: einGeniusData.project_key,
          is_enabled: einGeniusData.is_enabled
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('einGenius settings saved successfully');
      setEinGeniusConfigured(true);
      setEinGeniusStatusMessage('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save einGenius settings';
      toast.error(errorMsg);
    } finally {
      setSavingEinGenius(false);
    }
  };

  const buildAiTrainerPayload = () => ({
    ...aiTrainerData,
    providers: aiTrainerData.providers.map(provider => {
      const {
        temperature,
        max_tokens,
        max_output_tokens,
        top_p,
        presence_penalty,
        frequency_penalty,
        ...providerPayload
      } = provider;
      return {
        ...providerPayload,
        status: provider.provider === aiTrainerData.active_provider ? provider.status : 'Inactive'
      };
    })
  });

  const validateAiTrainerSettings = () => {
    const provider = activeAiProvider;
    if (!provider) {
      toast.error('Please select an AI provider');
      return false;
    }
    if (aiTrainerData.is_enabled && provider.status !== 'Active') {
      toast.error('The selected provider must be Active before enabling AI Trainer');
      return false;
    }
    if (aiTrainerData.is_enabled && !provider.api_key) {
      toast.error('Please enter the API key for the selected provider');
      return false;
    }
    if (aiTrainerData.is_enabled && !provider.model) {
      toast.error('Please select an AI model');
      return false;
    }
    return true;
  };

  const handleTestAiTrainer = async () => {
    if (!validateAiTrainerSettings()) return;
    setTestingAiTrainer(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/integration-settings/ai-trainer/test`,
        buildAiTrainerPayload(),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data?.message || 'AI provider connection successful');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI provider validation failed');
    } finally {
      setTestingAiTrainer(false);
    }
  };

  const handleSaveAiTrainer = async () => {
    if (!validateAiTrainerSettings()) return;
    setSavingAiTrainer(true);
    try {
      const token = localStorage.getItem('token');
      const payload = buildAiTrainerPayload();
      const response = await axios.put(
        `${API}/admin/integration-settings/ai-trainer`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('AI Integration settings saved successfully');
      setAiTrainerConfigured(response.data?.settings?.is_configured || false);
      fetchAiTrainerSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save AI Integration settings');
    } finally {
      setSavingAiTrainer(false);
    }
  };

  const handleUploadAiTrainerIcon = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('AI Trainer icon must be JPG, PNG, or WEBP');
      return;
    }
    setUploadingAiTrainerIcon(true);
    try {
      const token = localStorage.getItem('token');
      const uploadData = new FormData();
      uploadData.append('file', file);
      const response = await axios.post(
        `${API}/admin/integration-settings/ai-trainer/icon`,
        uploadData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setAiTrainerData(prev => ({
        ...prev,
        ai_trainer_icon_s3_key: response.data.ai_trainer_icon_s3_key || '',
        ai_trainer_icon_url: response.data.ai_trainer_icon_url || ''
      }));
      toast.success('AI Trainer icon uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload AI Trainer icon');
    } finally {
      setUploadingAiTrainerIcon(false);
    }
  };

  const handleRemoveAiTrainerIcon = async () => {
    setUploadingAiTrainerIcon(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/integration-settings/ai-trainer/icon`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiTrainerData(prev => ({
        ...prev,
        ai_trainer_icon_s3_key: '',
        ai_trainer_icon_url: ''
      }));
      toast.success('AI Trainer icon removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove AI Trainer icon');
    } finally {
      setUploadingAiTrainerIcon(false);
    }
  };
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Integration Settings</h1>
          <p className="text-gray-600 mt-2">
            Configure external service integrations for the Exlva HTMS platform
          </p>
        </div>
        
        {/* AWS S3 Configuration */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="px-6 md:px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
            <h2 className="text-xl font-semibold">AWS S3 Configuration</h2>
            <p className="text-blue-100 text-sm mt-1">
              Configure AWS S3 for secure file storage and access
            </p>
          </div>
          
          <div className="p-6 md:p-8">
            {/* Status Badge */}
            <div className="mb-6">
              {isConfigured ? (
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <span className="text-sm font-medium">S3 Configured</span>
                </div>
              ) : (
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-800">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  <span className="text-sm font-medium">S3 Not Configured</span>
                </div>
              )}
              {!isConfigured && s3StatusMessage && (
                <p className="text-sm text-amber-700 mt-2">{s3StatusMessage}</p>
              )}
            </div>
            
            {/* Form */}
            <div className="space-y-6 max-w-3xl">
              <div>
                <Label>AWS Access Key ID *</Label>
                <Input
                  type="text"
                  value={formData.aws_access_key}
                  onChange={(e) => handleInputChange('aws_access_key', e.target.value)}
                  placeholder="AKIAIOSFODNN7EXAMPLE"
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your AWS IAM user access key ID
                </p>
              </div>
              
              <div>
                <Label>AWS Secret Access Key *</Label>
                <div className="relative mt-2">
                  <Input
                    type={showSecretKey ? 'text' : 'password'}
                    value={formData.aws_secret_key}
                    onChange={(e) => handleInputChange('aws_secret_key', e.target.value)}
                    placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecretKey(!showSecretKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Your AWS IAM user secret access key (stored securely)
                </p>
              </div>
              
              <div>
                <Label>S3 Bucket Name *</Label>
                <Input
                  type="text"
                  value={formData.aws_bucket_name}
                  onChange={(e) => handleInputChange('aws_bucket_name', e.target.value)}
                  placeholder="exlva-training-content"
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  The S3 bucket where files will be stored
                </p>
              </div>
              
              <div>
                <Label>S3 Base URL</Label>
                <Input
                  type="text"
                  value={formData.aws_base_url}
                  onChange={(e) => handleInputChange('aws_base_url', e.target.value)}
                  placeholder="https://exlva.s3.ap-south-1.amazonaws.com/"
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional. If set, thumbnails will be returned as &lt;base_url&gt;/&lt;key&gt; (e.g., https://exlva.s3.ap-south-1.amazonaws.com/thumbnails/file.png)
                </p>
              </div>
              
              <div>
                <Label>AWS Region *</Label>
                <Input
                  type="text"
                  value={formData.aws_region}
                  onChange={(e) => handleInputChange('aws_region', e.target.value)}
                  placeholder="ap-south-1"
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  AWS region where your S3 bucket is located (e.g., ap-south-1, us-east-1)
                </p>
              </div>
              
              {/* Security Notice */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-900">Security Information</h4>
                    <ul className="text-sm text-blue-800 mt-2 space-y-1 list-disc list-inside">
                      <li>All uploaded files are stored privately in S3</li>
                      <li>Files are accessed using temporary signed URLs</li>
                      <li>Signed URLs expire after 1 hour for security</li>
                      <li>Your AWS credentials are encrypted in the database</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4">
                <Button
                  onClick={handleTestConnection}
                  variant="outline"
                  disabled={testing || saving}
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  {testing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <TestTube className="w-4 h-4 mr-2" />
                      Test Connection
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={handleSaveSettings}
                  disabled={testing || saving}
                  className="bg-[#0056B3] hover:bg-[#0056B3]/90"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* AI Trainer Integration */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mt-8">
          <div className="px-6 md:px-8 py-4 bg-gradient-to-r from-sky-600 to-cyan-600 text-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">AI Integration</h2>
                <p className="text-sky-100 text-sm mt-1">
                  Configure the provider used by AI Trainer for participant coaching
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 md:p-8">
            <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                {aiTrainerConfigured ? (
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">AI Trainer Configured</span>
                  </div>
                ) : (
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-800">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">AI Trainer Not Active</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Label htmlFor="ai-trainer-enabled" className="text-sm font-medium text-gray-700">
                  {aiTrainerData.is_enabled ? 'Enabled' : 'Disabled'}
                </Label>
                <Switch
                  id="ai-trainer-enabled"
                  checked={aiTrainerData.is_enabled}
                  onCheckedChange={(checked) => setAiTrainerData(prev => ({ ...prev, is_enabled: checked }))}
                />
              </div>
            </div>

            <div className="space-y-6 max-w-5xl">
              <div className="border border-gray-200 rounded-xl p-4 md:p-5">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="h-14 w-14 rounded-2xl bg-[#009B80]/12 flex items-center justify-center shrink-0 overflow-hidden border border-[#009B80]/15">
                      {aiTrainerData.ai_trainer_icon_url ? (
                        <img
                          src={aiTrainerData.ai_trainer_icon_url}
                          alt="AI Trainer icon"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Sparkles className="w-7 h-7 text-[#009B80]" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900">AI Trainer Icon</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Upload a custom icon for the AI Trainer panel shown to Staff and Reporting Managers.
                      </p>
                      {aiTrainerData.ai_trainer_icon_s3_key && (
                        <p className="text-xs text-gray-400 mt-1 truncate">S3: {aiTrainerData.ai_trainer_icon_s3_key}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <label className={`inline-flex items-center justify-center h-10 px-4 rounded-md text-sm font-semibold cursor-pointer ${
                      uploadingAiTrainerIcon ? 'bg-gray-200 text-gray-500' : 'bg-[#0056B3] text-white hover:bg-[#0056B3]/90'
                    }`}>
                      {uploadingAiTrainerIcon ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        'Upload Icon'
                      )}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleUploadAiTrainerIcon}
                        className="hidden"
                        disabled={uploadingAiTrainerIcon}
                      />
                    </label>
                    {aiTrainerData.ai_trainer_icon_s3_key && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleRemoveAiTrainerIcon}
                        disabled={uploadingAiTrainerIcon}
                        className="border-gray-300 text-gray-700"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {aiTrainerData.providers.map(provider => (
                  <button
                    key={provider.provider}
                    type="button"
                    onClick={() => handleAiTrainerProviderChange(provider.provider)}
                    className={`text-left rounded-xl border p-4 transition-colors ${
                      aiTrainerData.active_provider === provider.provider
                        ? 'border-[#0056B3] bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-900">{provider.provider_name}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {provider.provider === 'openai' ? 'ChatGPT-compatible Responses API' : 'Claude Messages API'}
                        </p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        provider.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {provider.status}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {activeAiProvider && (
                <div className="border border-gray-200 rounded-xl p-4 md:p-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <Label>Provider Name</Label>
                      <Input
                        value={activeAiProvider.provider_name || ''}
                        onChange={(e) => updateAiTrainerProvider(activeAiProvider.provider, 'provider_name', e.target.value)}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Status</Label>
                      <select
                        value={activeAiProvider.status || 'Inactive'}
                        onChange={(e) => updateAiTrainerProvider(activeAiProvider.provider, 'status', e.target.value)}
                        className="mt-2 w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Only the selected provider can be active.</p>
                    </div>

                    <div>
                      <Label>API Key *</Label>
                      <div className="relative mt-2">
                        <Input
                          type={showAiProviderKey ? 'text' : 'password'}
                          value={activeAiProvider.api_key || ''}
                          onChange={(e) => updateAiTrainerProvider(activeAiProvider.provider, 'api_key', e.target.value)}
                          placeholder={activeAiProvider.provider === 'openai' ? 'sk-...' : 'sk-ant-...'}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowAiProviderKey(!showAiProviderKey)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showAiProviderKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Stored server-side and masked after saving.</p>
                    </div>

                    <div>
                      <Label>Base URL</Label>
                      <Input
                        value={activeAiProvider.base_url || ''}
                        onChange={(e) => updateAiTrainerProvider(activeAiProvider.provider, 'base_url', e.target.value)}
                        placeholder={activeAiProvider.provider === 'openai' ? 'https://api.openai.com/v1' : 'https://api.anthropic.com'}
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label>Organization ID</Label>
                      <Input
                        value={activeAiProvider.organization_id || ''}
                        onChange={(e) => updateAiTrainerProvider(activeAiProvider.provider, 'organization_id', e.target.value)}
                        placeholder="Optional"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label>Project ID</Label>
                      <Input
                        value={activeAiProvider.project_id || ''}
                        onChange={(e) => updateAiTrainerProvider(activeAiProvider.provider, 'project_id', e.target.value)}
                        placeholder="Optional"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label>{activeAiProvider.provider === 'openai' ? 'OpenAI Model' : 'Claude Model'}</Label>
                      <select
                        value={activeAiProvider.model || ''}
                        onChange={(e) => updateAiTrainerProvider(activeAiProvider.provider, 'model', e.target.value)}
                        className="mt-2 w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {(activeAiProvider.supported_models || []).map(model => (
                          <option key={model} value={model}>{model}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label>{activeAiProvider.provider === 'openai' ? 'Skill / Assistant / GPT Selection' : 'Claude Skill / Workflow Selection'}</Label>
                      <Input
                        value={activeAiProvider.behavior_config || ''}
                        onChange={(e) => updateAiTrainerProvider(activeAiProvider.provider, 'behavior_config', e.target.value)}
                        placeholder={activeAiProvider.provider === 'openai' ? 'Assistant ID, GPT name, or prompt workflow key' : 'Claude skill or workflow key'}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="mt-5">
                    <Label>Supported Models</Label>
                    <textarea
                      value={(activeAiProvider.supported_models || []).join('\n')}
                      onChange={(e) => handleAiModelListChange(activeAiProvider.provider, e.target.value)}
                      className="mt-2 w-full min-h-[120px] rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">One model ID per line. The dropdown above is generated from this configurable list.</p>
                  </div>
                </div>
              )}

              <div>
                <Label>Master System Prompt</Label>
                <textarea
                  value={aiTrainerData.system_prompt}
                  onChange={(e) => setAiTrainerData(prev => ({ ...prev, system_prompt: e.target.value }))}
                  className="mt-2 w-full min-h-[120px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={DEFAULT_AI_TRAINER_PROMPT}
                />
                <p className="text-xs text-gray-500 mt-1">
                  The application adds participant name, employee ID, department, job role, hospital, language, progress and current learning context automatically at runtime.
                </p>
              </div>

              <div className="p-4 bg-sky-50 border border-sky-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-sky-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sky-900">Personalised AI Trainer Context</h4>
                    <p className="text-sm text-sky-800 mt-2">
                      AI Trainer sends learner identity, department, job role, hospital, preferred language and available training context to the active provider. API keys and prompts remain server-side, and every interaction is logged for audit reporting.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 pt-4">
                <Button
                  onClick={handleTestAiTrainer}
                  variant="outline"
                  disabled={testingAiTrainer || savingAiTrainer}
                  className="border-sky-600 text-sky-700 hover:bg-sky-50"
                >
                  {testingAiTrainer ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <TestTube className="w-4 h-4 mr-2" />
                      Test AI Provider
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleSaveAiTrainer}
                  disabled={testingAiTrainer || savingAiTrainer}
                  className="bg-sky-700 hover:bg-sky-800"
                >
                  {savingAiTrainer ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save AI Integration
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Legacy einGenius AI Trainer Fallback Configuration */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mt-8">
          <div className="px-6 md:px-8 py-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">einGenius AI Trainer Fallback</h2>
                <p className="text-purple-100 text-sm mt-1">
                  Optional legacy fallback when AI Integration is not active
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-6 md:p-8">
            {/* Status Badge */}
            <div className="mb-6 flex items-center justify-between">
              <div>
                {einGeniusConfigured ? (
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">Configured</span>
                  </div>
                ) : (
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-800">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">AI Configuration Missing</span>
                  </div>
                )}
                {!einGeniusConfigured && einGeniusStatusMessage && (
                  <p className="text-sm text-amber-700 mt-2">{einGeniusStatusMessage}</p>
                )}
              </div>
              
              {/* Enable/Disable Toggle */}
              <div className="flex items-center gap-3">
                <Label htmlFor="eingenius-enabled" className="text-sm font-medium text-gray-700">
                  {einGeniusData.is_enabled ? 'Enabled' : 'Disabled'}
                </Label>
                <Switch
                  id="eingenius-enabled"
                  checked={einGeniusData.is_enabled}
                  onCheckedChange={(checked) => handleEinGeniusChange('is_enabled', checked)}
                />
              </div>
            </div>
            
            {/* Form */}
            <div className="space-y-6 max-w-3xl">
              <div>
                <Label>Chat URL *</Label>
                <Input
                  type="text"
                  value={einGeniusData.chat_url}
                  onChange={(e) => handleEinGeniusChange('chat_url', e.target.value)}
                  placeholder="https://eingenius.ai/"
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  The base URL for the einGenius chat interface
                </p>
              </div>
              
              <div>
                <Label>API Endpoint URL *</Label>
                <Input
                  type="text"
                  value={einGeniusData.api_endpoint}
                  onChange={(e) => handleEinGeniusChange('api_endpoint', e.target.value)}
                  placeholder="https://eingenius.ai/projects/documents/chat_assistant/ms_addin_authentication/"
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  The API endpoint for user authentication with einGenius
                </p>
              </div>
              
              <div>
                <Label>Project Key (q value) *</Label>
                <Input
                  type="text"
                  value={einGeniusData.project_key}
                  onChange={(e) => handleEinGeniusChange('project_key', e.target.value)}
                  placeholder="6fc9c1ab08e55d17505e732d5b832ce5"
                  className="mt-2 font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">
                  The unique project identifier key for your einGenius instance
                </p>
              </div>
              
              {/* Info Box */}
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <MessageCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-purple-900">How it works</h4>
                    <ul className="text-sm text-purple-800 mt-2 space-y-1 list-disc list-inside">
                      <li>Staff and Reporting Managers will see a chat button on their dashboard</li>
                      <li>When they click it, their email and name are sent to einGenius for authentication</li>
                      <li>The chat interface opens in a floating window within the application</li>
                      <li>Disable the toggle above to hide the chat button from all users</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Save Button */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSaveEinGeniusSettings}
                  disabled={savingEinGenius}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {savingEinGenius ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save einGenius Settings
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        {/* SendGrid Email Configuration */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mt-8">
          <div className="px-6 md:px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">SendGrid Email API</h2>
                <p className="text-emerald-100 text-sm mt-1">
                  Configure SendGrid for all system email notifications
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-6 md:p-8">
            {/* Status Badge & Enable Toggle */}
            <div className="mb-6 flex items-center justify-between">
              <div>
                {sendgridConfigured ? (
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">Configured</span>
                  </div>
                ) : (
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-800">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">Not Configured</span>
                  </div>
                )}
                {!sendgridConfigured && sendgridStatusMessage && (
                  <p className="text-sm text-amber-700 mt-2">{sendgridStatusMessage}</p>
                )}
              </div>
              
              <div className="flex items-center gap-3">
                <Label htmlFor="sendgrid-enabled" className="text-sm font-medium text-gray-700">
                  {sendgridData.is_enabled ? 'Enabled' : 'Disabled'}
                </Label>
                <Switch
                  id="sendgrid-enabled"
                  checked={sendgridData.is_enabled}
                  onCheckedChange={(checked) => handleSendgridChange('is_enabled', checked)}
                />
              </div>
            </div>
            
            {/* Form */}
            <div className="space-y-6 max-w-3xl">
              <div>
                <Label>SendGrid API Key *</Label>
                <div className="relative mt-2">
                  <Input
                    type={showSendgridKey ? 'text' : 'password'}
                    value={sendgridData.api_key}
                    onChange={(e) => handleSendgridChange('api_key', e.target.value)}
                    placeholder="SG.xxxxxxxxxxxxxxxxxxxx"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSendgridKey(!showSendgridKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showSendgridKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Your SendGrid API key with Mail Send permission
                </p>
              </div>
              
              <div>
                <Label>Sender Email *</Label>
                <Input
                  type="email"
                  value={sendgridData.sender_email}
                  onChange={(e) => handleSendgridChange('sender_email', e.target.value)}
                  placeholder="noreply@yourdomain.com"
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must be a verified sender in your SendGrid account
                </p>
              </div>
              
              <div>
                <Label>Sender Name</Label>
                <Input
                  type="text"
                  value={sendgridData.sender_name}
                  onChange={(e) => handleSendgridChange('sender_name', e.target.value)}
                  placeholder="Exlva Support"
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Display name shown in the "From" field of emails
                </p>
              </div>
              
              <div>
                <Label>Reply-To Email</Label>
                <Input
                  type="email"
                  value={sendgridData.reply_to_email}
                  onChange={(e) => handleSendgridChange('reply_to_email', e.target.value)}
                  placeholder="support@yourdomain.com"
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional. Email address recipients can reply to
                </p>
              </div>
              
              {/* Info Box */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-emerald-900">How it works</h4>
                    <ul className="text-sm text-emerald-800 mt-2 space-y-1 list-disc list-inside">
                      <li>All system emails (welcome, OTP, notifications) will use this configuration</li>
                      <li>Sender email must be verified in your SendGrid account</li>
                      <li>API key is stored securely and never exposed after saving</li>
                      <li>Use "Test Connection" to verify setup before enabling</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4">
                <Button
                  onClick={handleTestSendgrid}
                  variant="outline"
                  disabled={testingSendgrid || savingSendgrid}
                  className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                >
                  {testingSendgrid ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <TestTube className="w-4 h-4 mr-2" />
                      Test Connection
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={handleSaveSendgrid}
                  disabled={testingSendgrid || savingSendgrid}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {savingSendgrid ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save SendGrid Settings
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Help Subchapter */}
        <div className="mt-6 bg-gray-50 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-3">How to get AWS credentials:</h3>
          <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
            <li>Log in to AWS Management Console</li>
            <li>Go to IAM (Identity and Access Management)</li>
            <li>Create a new IAM user or use an existing one</li>
            <li>Attach the <code className="px-1 py-0.5 bg-gray-200 rounded">AmazonS3FullAccess</code> policy</li>
            <li>Generate access keys in the Security Credentials tab</li>
            <li>Copy the Access Key ID and Secret Access Key</li>
          </ol>
        </div>
      </div>
    </AdminLayout>
  );
};

export default IntegrationSettingsPage;
